import { ActionTable, PermissionCheck } from '@/components';
import { useATSelection } from '@/components/table/hooks';
import CreatePurchaseOrder from '@/features/Procurement/CreatePurchaseOrder';
import DraftOrderManageCard from '@/features/Procurement/DraftOrderManageCard';
import { useAppSelector } from '@/state';
import { ProcurementApi } from '@/state/api';
import { ProcurementType } from '@/types';
import { permissionsKeys } from '@/types/permissionsType';
import { Box, Button, Center, Flex, Text } from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { Card, OverlayDialog, toasterApi } from '@ui';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import z from 'zod';
import EditDraftOrderLine from '../EditDraftOrderLine';

type FulfilDraftOrders = {
  draftOrderId: number | undefined;
  onClose: () => void;
};

const FulfilDraftOrders = ({ draftOrderId, onClose }: FulfilDraftOrders) => {
  // Get current user
  const currentUser = useAppSelector((state) => state.auth.current.user);

  // Create PO modal
  const [showCreatePOModal, setShowCreatePOModal] = useState<boolean>(false);

  // Draft order line table selected row
  const { selection } = useATSelection();

  const [selectedDraftOrderLine, setSelectedDraftOrderLine] =
    useState<GetDraftOrderLineProps | null>(null);

  // Get current draft order
  const { data: getDraftOrder, isLoading: isLoadingGetDraftOrder } =
    ProcurementApi.Api.useGetDraftOrderQuery(
      draftOrderId
        ? {
            params: {
              id: draftOrderId,
            },
          }
        : skipToken,
    );

  const validSelectedDraftOrderLine = useMemo(() => {
    // If no draft order lines, return undefined
    if (!getDraftOrder?.lines?.length) return undefined;

    // If no draft order line is selected, return the first draft order line
    if (!selectedDraftOrderLine) return getDraftOrder?.lines[0];

    // If a draft order line is selected, return the selected draft order line
    return getDraftOrder?.lines?.find(
      (line) => line.id === selectedDraftOrderLine.id,
    );

    // return selectedDraftOrderLine;
  }, [selectedDraftOrderLine, getDraftOrder?.lines]);

  // Delete draft order line mutation
  const [deleteDraftOrderLineMutation] =
    ProcurementApi.Api.useDeleteDraftOrderLineMutation();

  // Overlay dialog for adding new draft order lines
  const handleAddNewDraftOrderLine = () => {
    OverlayDialog.open('addNewDraftOrderLine', {
      overlayId: 'addNewDraftOrderLine',
      title: 'Add New Draft Order Line',
      description: 'Add new lines to the current draft order.',

      content: (
        <DraftOrderManageCard
          isNew={false}
          selectedDraftOrderId={draftOrderId}
          onClose={() => OverlayDialog.close('addNewDraftOrderLine')}
        />
      ),
    });
  };

  // Overlay dialog for deleting draft order line
  const handleDeleteDraftOrderLine = (id: number) => {
    OverlayDialog.open('deleteDraftOrderLine', {
      overlayId: 'deleteDraftOrderLine',
      title: 'Delete Draft Order Line',
      description: `Are you sure you want to delete draft order line with id-${id}? This action cannot be undone.`,
      handleConfirm: async () => {
        await deleteDraftOrderLineMutation({
          params: {
            id,
          },
        })
          .unwrap()
          .then(() => {
            setSelectedDraftOrderLine(null);
            toasterApi({
              type: 'success',
              status: 200,
              code: 'Success',
              message: 'Draft order line deleted successfully',
            });
          })
          .catch(() => {
            toasterApi({
              type: 'error',
              status: 500,
              code: 'Error',
              message: 'Could not delete draft order line',
            });
          })
          .finally(() => {
            OverlayDialog.close('deleteDraftOrderLine');
          });
      },
    });
  };

  const draftOrderLineTableColumns: ActionTable.ColumnsConfigType<
    GetDraftOrderLineProps,
    { actions: null }
  > = [
    {
      id: {
        label: 'Id',
        width: '100px',
      },
    },
    {
      part_number: {
        label: 'Part #',
        width: '100px',
      },
    },
    {
      qty: {
        label: 'Qty',
        width: '100px',
      },
    },
    {
      unit_price: {
        label: 'Unit price',
        width: '100px',
      },
    },
    {
      needed_by: {
        label: 'Needed by',
        width: '200px',
      },
    },
    {
      draft: {
        label: 'Draft #',
        width: '100px',
      },
    },
    {
      notes: {
        label: 'Notes',
        width: '200px',
      },
    },
    ...(currentUser?.permissions?.some(
      (permission) => permission === permissionsKeys.delete_purchaseorder,
    )
      ? [
          {
            actions: {
              label: 'Actions',
              width: '120px',
            },
          },
        ]
      : []),
  ];

  const renderCell = (
    props: ActionTable.RenderCellType<
      GetDraftOrderLineProps,
      { actions: null }
    >,
  ) => {
    const { colKey, cellValue, rowValue } = props;

    switch (colKey) {
      case 'needed_by':
        return <Text>{dayjs(cellValue).format('DD MMM YYYY, hh:mm a')}</Text>;
      case 'actions': {
        const item = rowValue;
        return (
          <Button
            size="sm"
            colorPalette="red"
            onClick={() => handleDeleteDraftOrderLine(item?.id)}
          >
            Delete
          </Button>
        );
      }
      default:
        return cellValue?.value ?? cellValue ?? '';
    }
  };

  return !showCreatePOModal ? (
    <Flex flexDirection="column" gapY={4}>
      <Card>
        <Flex justifyContent="space-between">
          <Flex flexDirection="column" gapY={2}>
            <Text fontWeight="bold">
              {`${getDraftOrder?.supplier} - Fulfill draft orders`}
            </Text>
          </Flex>
          <Button size="sm" bg="fg" color="fg.inverted" onClick={onClose}>
            Back
          </Button>
        </Flex>
      </Card>
      <Flex flexDirection="column" gapY={4}>
        <Flex flex={1}>
          <Card>
            <Flex flexDirection="column" gapY={2}>
              <Flex flexDirection="column" gapY={2}>
                <Text fontWeight="bold" fontSize="sm">
                  Draft Order lines
                </Text>
                <PermissionCheck
                  permissions={[
                    permissionsKeys.add_purchaseorder,
                    permissionsKeys.change_purchaseorder,
                  ]}
                >
                  <Flex justifyContent="space-between">
                    <Button size="sm" onClick={handleAddNewDraftOrderLine}>
                      Add new lines
                    </Button>

                    <Button
                      size="sm"
                      color="fg.inverted"
                      bg="fg"
                      disabled={!getDraftOrder?.lines?.length}
                      onClick={() => setShowCreatePOModal(true)}
                    >
                      Convert to PO
                    </Button>
                  </Flex>
                </PermissionCheck>
              </Flex>

              <Center>
                <Box maxWidth="fit-content">
                  <Flex flexDirection="column" gapY={2}>
                    <ActionTable.FilterBar
                      filterConfigComponents={
                        filterConfigComponentsDraftOrderLines
                      }
                    />
                    <Box display="flex" flexDirection="row" gap={5}>
                      <Box h="100%" my={5}>
                        <ActionTable<GetDraftOrderLineProps, { actions: null }>
                          width="76.5vw"
                          columns={draftOrderLineTableColumns}
                          columnToUseAsRowKey="id"
                          items={getDraftOrder?.lines ?? []}
                          isPaginated={false}
                          isPaginatedAsync={false}
                          totalItemsCount={getDraftOrder?.lines?.length ?? 0}
                          isSortable={true}
                          isFilterable={true}
                          isShowActionBar={false}
                          height={'35vh'}
                          rowHeight={60}
                          isLoading={isLoadingGetDraftOrder}
                          isMultipleSelection={false}
                          onRowClick={(row) => {
                            selection.set([row], 'id');
                            setSelectedDraftOrderLine(row);
                          }}
                          renderCell={renderCell}
                        />
                      </Box>
                    </Box>
                  </Flex>
                </Box>
              </Center>
            </Flex>
          </Card>
        </Flex>
        <Flex flex={1}>
          <Card>
            <Flex flexDirection="column" gapY={4}>
              {/* Edit draft order line form */}
              <EditDraftOrderLine
                isEditEnabled={currentUser?.permissions?.some(
                  (permission) =>
                    permission === permissionsKeys.change_purchaseorder,
                )} // check if user has permission to edit draft order. If not, disable edit draft order line form
                draftOrder={getDraftOrder}
                lineId={validSelectedDraftOrderLine?.id}
              />
            </Flex>
          </Card>
        </Flex>
      </Flex>
    </Flex>
  ) : (
    <CreatePurchaseOrder
      onClickBack={() => {
        setShowCreatePOModal(false);
      }}
      onSubmit={() => {
        setShowCreatePOModal(false);
        onClose();
      }}
      draftOrder={getDraftOrder}
      draftOrderLines={getDraftOrder?.lines?.map(
        ({ needed_by, ...line }, index) => ({
          index: index + 1,
          needed_by: dayjs(needed_by).toISOString(),
          ...line,
        }),
      )}
      draftId={getDraftOrder?.id as number}
      release_numbers={getDraftOrder?.release_numbers ?? []}
    />
  );
};
export default FulfilDraftOrders;

type GetDraftOrderLineProps =
  ProcurementType.DraftOrders.Get['response'][number]['lines'][number];

const filterConfigComponentsDraftOrderLines: ActionTable.FilterConfigComponentType<GetDraftOrderLineProps>[] =
  [
    {
      filterComponent: 'input',
      filterComponentLabel: 'Part Number',
      filterConfigItems: [
        {
          colKeys: ['part_number'],
          value: '',
          operator: 'contains',
        },
      ],
    },
  ];

const draftOrderLineSchema = z
  .object({
    ...ProcurementType.DraftOrderLines.draftOrderLineBaseSchema.shape,
    part_type: z.enum(['system', 'manual']),
    part: z.preprocess((val) => {
      // Treat empty values or 0 as undefined
      if (val === '' || val === null || val === undefined || val === 0)
        return undefined;
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    }, z.number().positive().optional()),
    manual_part_number: z.string().optional(),
  })
  .refine(
    (line) =>
      line.part_type !== 'system' || (line.part != null && line.part > 0),
    {
      message: 'Part is required',
      path: ['part'],
    },
  )
  .refine(
    (line) =>
      line.part_type !== 'manual' ||
      (line.manual_part_number != null &&
        line.manual_part_number.trim() !== ''),
    {
      message: 'Manual part number is required',
      path: ['manual_part_number'],
    },
  );
const draftOrderLinesFormSchema = z.object({
  draftOrderLines: z.array(draftOrderLineSchema),
});
type DraftOrderLinesFormType = z.infer<typeof draftOrderLinesFormSchema>;
